import type { ReactNode } from "react";
import {
  equipmentBrands,
  equipmentCatalog,
  equipmentModelById,
  type EquipmentKind,
} from "@voice/domain";

export type EquipmentSelection = { brandId: string; modelId: string };
export const blankEquipmentSelection: EquipmentSelection = {
  brandId: "",
  modelId: "",
};
export function selectEquipmentBrand(brandId: string): EquipmentSelection {
  return { brandId, modelId: brandId === "other" ? "other" : "" };
}
export function selectEquipmentModel(
  selection: EquipmentSelection,
  modelId: string,
): EquipmentSelection {
  const entry = equipmentModelById(modelId);
  return {
    ...selection,
    modelId:
      modelId === "other" || entry?.brandId === selection.brandId
        ? modelId
        : "",
  };
}
export const equipmentKindLabels: Record<EquipmentKind, string> = {
  sip_phone: "SIP phone",
  ata: "Analog telephone adapter (ATA)",
  gateway: "Gateway",
  other: "Other equipment",
};
function Field({ title, children }: { title: string; children: ReactNode }) {
  return (
    <label className="cr-field">
      <span>{title}</span>
      {children}
    </label>
  );
}
export function EquipmentModelFields({
  selection,
  onChange,
}: {
  selection: EquipmentSelection;
  onChange: (selection: EquipmentSelection) => void;
}) {
  const entry = equipmentModelById(selection.modelId);
  const brand = equipmentBrands.find((item) => item.id === selection.brandId);
  const custom = selection.modelId === "other";
  return (
    <>
      <Field title="Brand">
        <select
          required
          value={selection.brandId}
          onChange={(event) =>
            onChange(selectEquipmentBrand(event.target.value))
          }
        >
          <option value="">Choose a brand</option>
          {equipmentBrands.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
          <option value="other">Other brand</option>
        </select>
      </Field>
      {selection.brandId === "other" ? (
        <>
          <input type="hidden" name="catalogModelId" value="other" />
          <Field title="Brand name">
            <input
              key={selection.brandId}
              name="manufacturer"
              required
              maxLength={40}
            />
          </Field>
        </>
      ) : (
        <Field title="Model">
          <select
            name="catalogModelId"
            required
            disabled={!brand}
            value={selection.modelId}
            onChange={(event) =>
              onChange(selectEquipmentModel(selection, event.target.value))
            }
          >
            <option value="">
              {brand ? "Choose a model" : "Choose a brand first"}
            </option>
            {equipmentCatalog
              .filter((item) => item.brandId === selection.brandId)
              .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.model}
                </option>
              ))}
            {brand && <option value="other">Other model</option>}
          </select>
        </Field>
      )}
      {custom && (
        <>
          {brand && (
            <input
              type="hidden"
              name="manufacturer"
              value={brand.id === "poly" ? "Poly" : brand.name}
            />
          )}
          <Field title="Model name">
            <input
              key={selection.brandId}
              name="model"
              required
              maxLength={79}
            />
          </Field>
          <Field title="Equipment type">
            <select name="equipmentKind" defaultValue="sip_phone">
              {Object.entries(equipmentKindLabels).map(([value, name]) => (
                <option key={value} value={value}>
                  {name}
                </option>
              ))}
            </select>
          </Field>
        </>
      )}
      {entry && (
        <>
          <Field title="Equipment type">
            <output>{equipmentKindLabels[entry.kind]}</output>
          </Field>
          {entry.note && (
            <p className="cr-form-note" role="status">
              {entry.note}
            </p>
          )}
        </>
      )}
      {custom && (
        <p className="cr-form-note">
          We’ll save the brand and model you enter. Its provisioning
          configuration needs review.
        </p>
      )}
    </>
  );
}
